import { approximateFraction } from './approx_fraction';

// 1.2 - 1.0 === 0.19999999999999996
// fixFloatError(1.2 - 1.0) === 0.2
const fixFloatError = (n) => parseFloat(n.toPrecision(12));

// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/round
function decimalAdjust(type, value, exp) {
  // If the exp is undefined or zero...
  if (typeof exp === 'undefined' || +exp === 0) {
    return Math[type](value);
  }
  value = +value;
  exp = +exp;
  // If the value is not a number or the exp is not an integer...
  if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) {
    return NaN;
  }
  // If the value is negative...
  if (value < 0) {
    return -decimalAdjust(type, -value, exp);
  }
  // Shift
  value = value.toString().split('e');
  value = Math[type](+(value[0] + 'e' + (value[1] ? +value[1] - exp : -exp)));
  // Shift back
  value = value.toString().split('e');

  return +(value[0] + 'e' + (value[1] ? +value[1] + exp : exp));
}

const FORMATS = {
  // American format
  american: {
    from: (american) => {
      american = parseFloat(american);
      if (american >= 0) {
        return american / 100.0 + 1;
      }
      return 100 / -american + 1;
    },
    to: (decimalValue) => {
      if (decimalValue >= 2) {
        return fixFloatError((decimalValue - 1) * 100.0);
      }
      return fixFloatError(-100 / (decimalValue - 1));
    },
  },
  // European/Decimal format
  decimal: {
    from: (decimal) => {
      decimal = parseFloat(decimal);
      if (decimal < 1.0) {
        console.error('Outside valid range.');
      }
      return decimal;
    },
    to: (decimalValue) => decimalValue,
  },
  // UK/Fractional format
  fractional: {
    from: (n) => {
      // Try to split on the slash
      const pieces = n.toString().split('/');
      n = parseFloat(pieces[0]);
      let d;
      if (pieces.length === 2) {
        d = parseFloat(pieces[1]);
      } else if (pieces.length === 1) {
        d = 1;
      } else {
        console.error('Invalid fraction');
      }

      if (n === 0 || d === 0 || n / d <= 0.0) {
        console.error('Outside valid range');
      }
      return 1 + n / d;
    },
    to: (decimalValue, options) => approximateFraction(decimalValue - 1, options.precision || 12),
  },
  // Hong Kong format
  hongKong: {
    from: (hongKong) => {
      hongKong = parseFloat(hongKong);
      if (hongKong < 0.0) {
        console.error('Outside valid range.');
      }
      return hongKong + 1.0;
    },
    to: (decimalValue) => fixFloatError(decimalValue - 1),
  },
  // Implied probability
  impliedProbability: {
    from: (ip) => {
      // Handle percentage string
      if (typeof ip === 'string' && ip.slice(-1) == '%') {
        ip = parseFloat(ip) / 100.0;
      } else {
        ip = parseFloat(ip);
      }

      if (ip <= 0.0 || ip >= 1.0) {
        console.error('Outside valid range');
      }
      return 1.0 / ip;
    },
    to: (decimalValue, options) => {
      if (options.percentage) {
        let value = fixFloatError(100.0 / decimalValue);
        // HACK: Oddslib.prototype.to calls decimalAdjust if we return a number.
        // But we need to round before adding the % symbol.
        // So we do it here and return the string
        if (options.precision !== null) {
          value = decimalAdjust('round', value, -options.precision);
        }

        return value.toString() + '%';
      }

      return fixFloatError(1 / decimalValue);
    },
  },
  // Indonesian format
  indonesian: {
    from: (indonesian) => {
      indonesian = parseFloat(indonesian);

      if (indonesian === 0) {
        console.error('Outside valid range.');
      }

      if (indonesian >= 1) {
        return indonesian + 1;
      }
      return -1 / indonesian + 1;
    },
    to: (decimalValue) => {
      if (decimalValue < 2.0) {
        return fixFloatError(-1 / (decimalValue - 1));
      }
      return fixFloatError(decimalValue - 1);
    },
  },
  // Malaysian format
  malaysian: {
    from: (malaysian) => {
      malaysian = parseFloat(malaysian);

      if (malaysian <= -1.0 || malaysian > 1.0) {
        console.error('Outside valid range.');
      }

      if (malaysian < 0) {
        malaysian = -1 / malaysian;
      }
      return malaysian + 1;
    },
    to: (decimalValue) => {
      if (decimalValue <= 2.0) {
        return fixFloatError(decimalValue - 1);
      }
      return fixFloatError(-1 / (decimalValue - 1));
    },
  },
};

const Odds = (function () {
  // Private constructor pattern
  // from http://stackoverflow.com/a/21731713
  const PublicOdds = function () {
    console.error('This constructor is private, please use the from* functions');
  };
  const Odds = function (decimalValue) {
    if (typeof decimalValue !== 'number' || isNaN(decimalValue)) {
      console.error('Invalid odds');
    }
    this.decimalValue = fixFloatError(decimalValue);
  };

  Odds.prototype = PublicOdds.prototype;

  // Generic constructor
  PublicOdds.from = function (format, value) {
    if (!Object.hasOwn(FORMATS, format)) {
      console.error('Unknown format ' + format + '.');
    }
    const decimal = FORMATS[format].from(value);
    return new Odds(decimal);
  };

  return PublicOdds;
})();

// Conversion API
Odds.prototype.to = function (format, options) {
  if (!Object.hasOwn(FORMATS, format)) {
    console.error('Unknown format ' + format + '.');
  }

  options = {
    percentage: false,
    precision: null,
    ...options,
  };

  const ret = FORMATS[format].to(this.decimalValue, options);
  if (typeof ret === 'number' && options.precision !== null) {
    return decimalAdjust('round', ret, -options.precision);
  }
  return ret;
};

export const oddsLib = { Odds, from: Odds.from };
